/**
 * 답안 입력 컴포넌트 셀렉터
 * 답형식에 따라 적절한 입력 컴포넌트 렌더링
 */

import React from 'react';
import { AnswerFormat } from '../../types/problem';
import { AnswerValue } from '../../types/answer';
import { MultipleChoiceInput } from './MultipleChoiceInput';
import { SelectionInput } from './SelectionInput';
import { OXInput } from './OXInput';
import { NumberInput } from './NumberInput';
import { EquationInput } from './EquationInput';
import { EssayInput } from './EssayInput';
import { HandwritingInput } from './HandwritingInput';

/**
 * AnswerInputSelector Props
 */
interface AnswerInputSelectorProps {
  /** 답형식 */
  answerFormat: AnswerFormat;
  /** 답안 제출 콜백 */
  onSubmit: (answer: AnswerValue) => void;
  /** 비활성화 여부 */
  disabled?: boolean;
}

/**
 * AnswerInputSelector 컴포넌트
 *
 * - 답형식(M/S/B/N/E/F/T/H)에 따라 적절한 입력 UI 렌더링
 * - 각 타입별 컴포넌트로 분기
 * - 공통 인터페이스로 답안 제출
 */
export const AnswerInputSelector: React.FC<AnswerInputSelectorProps> = ({
  answerFormat,
  onSubmit,
  disabled = false,
}) => {
  /**
   * 답형식별 컴포넌트 렌더링
   */
  switch (answerFormat) {
    case 'M':
      // 객관식 (5지선다)
      return (
        <MultipleChoiceInput
          onSubmit={(answer) => onSubmit(answer)}
          disabled={disabled}
        />
      );

    case 'S':
      // 보기선택 (다중선택)
      return (
        <SelectionInput
          onSubmit={(answer) => onSubmit(answer)}
          disabled={disabled}
        />
      );

    case 'B':
      // OX 문제
      return <OXInput onSubmit={(answer) => onSubmit(answer)} disabled={disabled} />;

    case 'N':
      // 숫자 입력
      return (
        <NumberInput onSubmit={(answer) => onSubmit(answer)} disabled={disabled} />
      );

    case 'E':
    case 'F':
    case 'T':
    case 'H':
      // E/F/T/H타입은 SolvePage에서 직접 처리 (Canvas 필요)
      return (
        <div className="text-center py-8">
          <p className="font-pixel text-sm text-gray-400">
            {answerFormat}타입은 특수 처리됩니다
          </p>
        </div>
      );

    default:
      // 알 수 없는 답형식
      return (
        <div className="text-center py-8">
          <p className="font-pixel text-sm text-red-500">
            알 수 없는 답형식: {answerFormat}
          </p>
        </div>
      );
  }
};
